/**
 * ProjectService - save/load/clear projects
 */

import { ApiBaseWeb } from '../ui/services/ApiBaseWeb';
import { webRuntimeApi } from '../runtime/web';
import type { TweaksFile } from '../nodes/PresetManager';

export interface FileTreeNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children?: FileTreeNode[];
}

class ProjectService extends ApiBaseWeb {
  async list(): Promise<string[]> {
    const response = await this.get<{ projects: string[] }>('library/projects');
    return response.projects;
  }

  async listTree(): Promise<FileTreeNode> {
    const response = await this.get<{ tree: FileTreeNode }>('library/projects/tree');
    return response.tree;
  }

  async save(name: string): Promise<void> {
    // Always send local session state so saves work even with sync disabled
    const session = webRuntimeApi.getLocalSession();
    await this.post(`library/project/${encodeURIComponent(name)}`, { session });
  }

  async load(name: string): Promise<void> {
    if (webRuntimeApi.syncEnabled) {
      // Sync ON: Load into server session, websocket will sync to all clients
      await this.post(`library/project/${encodeURIComponent(name)}/load`, {});
    } else {
      // Sync OFF: Fetch project and apply locally only
      const project = await this.get<{
        definitions: Record<string, any>;
        positions?: Record<string, { x: number; y: number }>;
        viewport?: Record<string, { x: number; y: number; zoom: number }>;
      }>(`library/project/${encodeURIComponent(name)}`);
      webRuntimeApi.loadProject(project);
    }
  }

  async clear(): Promise<void> {
    await this.post('clear', {});
  }

  /** Reset to empty project (local only) */
  new(): void {
    webRuntimeApi.newProject();
  }

  async loadTweaks(name: string): Promise<TweaksFile> {
    return this.get<TweaksFile>(`library/project/${encodeURIComponent(name)}/tweaks`);
  }

  async saveTweaks(name: string, tweaks: TweaksFile): Promise<void> {
    await this.post(`library/project/${encodeURIComponent(name)}/tweaks`, { tweaks });
  }
}

export const projectService = new ProjectService();
